import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Clock, Users, Phone, User, Mail, MessageSquare, ArrowRight } from "lucide-react";
import { RESTAURANT, IMAGES } from "../data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMES = [
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30",
  "20:00", "20:30", "21:00", "21:30", "22:00",
];

const today = () => new Date().toISOString().slice(0, 10);

export default function Book() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    party_size: 2,
    date: today(),
    time: "19:00",
    notes: "",
  });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) =>
    setForm({ ...form, [k]: k === "party_size" ? Number(e.target.value) : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/reservations`, {
        ...form,
        email: form.email || null,
        notes: form.notes || null,
      });
      toast.success(
        `Reservation requested for ${form.party_size} on ${form.date} at ${form.time}. We'll be in touch.`
      );
      setForm({
        name: "",
        phone: "",
        email: "",
        party_size: 2,
        date: today(),
        time: "19:00",
        notes: "",
      });
    } catch {
      toast.error("Couldn't submit your reservation. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="bg-[#0A0A0A] pt-[180px] pb-24" data-testid="book-page">
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.bar}
            alt=""
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-[#D4A017] uppercase tracking-[0.3em] text-xs">Reservations</span>
          <h1 className="font-impact uppercase text-white text-5xl md:text-7xl mt-3 tracking-tight">
            Book a <span className="text-[#D4A017]">Table</span>
          </h1>
          <p className="text-white/65 mt-5 max-w-2xl mx-auto">
            Reserve your spot at Aradelá. We'll confirm your booking by phone or text shortly after you submit.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 mt-8">
        <div className="grid md:grid-cols-12 gap-8 items-start">
          {/* Form */}
          <form
            onSubmit={submit}
            className="md:col-span-8 bg-[#141414] border border-white/5 p-8 md:p-10"
            data-testid="reservation-form"
          >
            <h2 className="font-display text-white text-2xl mb-6">Your booking details</h2>

            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <Field label="Name" icon={User}>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="book-name"
                  className="bg-transparent text-white focus:outline-none flex-1 placeholder:text-white/30"
                  placeholder="Full name"
                />
              </Field>
              <Field label="Phone" icon={Phone}>
                <input
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="book-phone"
                  className="bg-transparent text-white focus:outline-none flex-1 placeholder:text-white/30"
                  placeholder="07…"
                  type="tel"
                />
              </Field>
            </div>

            <Field label="Email (optional)" icon={Mail}>
              <input
                value={form.email}
                onChange={update("email")}
                data-testid="book-email"
                className="bg-transparent text-white focus:outline-none flex-1 placeholder:text-white/30"
                placeholder="you@email.com"
                type="email"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
              <Field label="Party Size" icon={Users}>
                <select
                  value={form.party_size}
                  onChange={update("party_size")}
                  data-testid="book-party-size"
                  className="bg-transparent text-white focus:outline-none flex-1"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20].map((n) => (
                    <option key={n} value={n} className="bg-[#141414]">
                      {n} {n === 1 ? "guest" : "guests"}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Date" icon={Calendar}>
                <input
                  required
                  type="date"
                  min={today()}
                  value={form.date}
                  onChange={update("date")}
                  data-testid="book-date"
                  className="bg-transparent text-white focus:outline-none flex-1 [color-scheme:dark]"
                />
              </Field>
              <Field label="Time" icon={Clock}>
                <select
                  value={form.time}
                  onChange={update("time")}
                  data-testid="book-time"
                  className="bg-transparent text-white focus:outline-none flex-1"
                >
                  {TIMES.map((t) => (
                    <option key={t} value={t} className="bg-[#141414]">
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Notes (allergies, occasion, accessibility)" icon={MessageSquare} multiline>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={update("notes")}
                  data-testid="book-notes"
                  className="bg-transparent text-white focus:outline-none flex-1 placeholder:text-white/30 resize-none w-full"
                  placeholder="Optional"
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="book-submit"
              className="mt-8 inline-flex items-center gap-2 bg-[#006400] hover:bg-[#0E7A36] disabled:opacity-60 text-white font-bold uppercase tracking-[0.12em] px-7 py-4 text-sm rounded-full transition-colors"
            >
              {sending ? "Sending…" : "Request Reservation"}
              <ArrowRight size={16} />
            </button>
            <p className="text-white/45 text-xs mt-4">
              By submitting, you agree we may contact you about your booking. We'll confirm availability shortly.
            </p>
          </form>

          {/* Info side */}
          <aside className="md:col-span-4 space-y-4" data-testid="book-info">
            <div className="bg-[#141414] border border-white/5 p-6">
              <h3 className="font-impact uppercase text-[#D4A017] text-sm tracking-wider mb-3">
                Opening Hours
              </h3>
              <ul className="space-y-1.5 text-white/75 text-sm">
                {RESTAURANT.hours.map((h) => (
                  <li key={h.day} className="flex justify-between gap-3">
                    <span className="text-white/55">{h.day}</span>
                    <span>{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <a
              href={`tel:${RESTAURANT.phoneRaw}`}
              data-testid="book-call-card"
              className="bg-[#141414] border border-white/5 p-6 flex items-center gap-4 hover:border-[#D4A017]/50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-[#D4A017] text-black flex items-center justify-center">
                <Phone size={16} />
              </div>
              <div>
                <div className="text-white/55 text-xs uppercase tracking-[0.2em]">Or call</div>
                <div className="text-white font-medium group-hover:text-[#D4A017] transition-colors">
                  {RESTAURANT.phone}
                </div>
              </div>
            </a>

            <div className="bg-[#141414] border border-white/5 p-6">
              <h3 className="font-impact uppercase text-[#D4A017] text-sm tracking-wider mb-3">
                Good to know
              </h3>
              <ul className="text-white/65 text-sm space-y-2">
                <li>• Reservations subject to availability.</li>
                <li>• Larger groups (12+) — please call us directly.</li>
                <li>• Allergies / dietary needs — please add to notes.</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

const Field = ({ label, icon: Icon, children, multiline = false }) => (
  <label className="block">
    <span className="text-white/50 text-[11px] uppercase tracking-[0.2em] block mb-2">
      {label}
    </span>
    <div
      className={`bg-white/5 border border-white/15 px-4 ${multiline ? "py-3" : "py-3"} flex ${
        multiline ? "items-start" : "items-center"
      } gap-3 focus-within:border-[#D4A017] transition-colors`}
    >
      <Icon size={16} className="text-[#D4A017] flex-shrink-0 mt-0.5" />
      {children}
    </div>
  </label>
);
